// CartContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
// Import the createCheckout function from your Shopify service file
import { createCartAndAddItems, redirectToCheckout } from '../services/shopify';
// Import the mobile detection utility
import { isMobile } from '../utils/device';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);

  // Load cart from localStorage on initial render
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  // Save cart to localStorage and update cart count/total whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
    setCartCount(cart.reduce((total, item) => total + item.quantity, 0));
    setCartTotal(cart.reduce((total, item) => total + item.price * item.quantity, 0));
  }, [cart]);

  // Add item to cart
  const addToCart = (product, variant, quantity = 1) => {
    setCart((prevCart) => {
      // Handle case where variant is just the ID string
      const variantId = typeof variant === 'string' ? variant : variant.id;
      
      const existingItem = prevCart.find((item) => item.variantId === variantId);
      if (existingItem) {
        // Update quantity if item exists
        return prevCart.map((item) =>
          item.variantId === variantId
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        // Add new item; retrieve its image from product data
        const imageUrl = product.images.edges[0]?.node.src || '';
        const price = typeof variant === 'string' 
          ? parseFloat(product.variants.edges.find(e => e.node.id === variantId)?.node.price.amount || '0')
          : parseFloat(variant.price.amount);
        const variantTitle = typeof variant === 'string'
          ? product.variants.edges.find(e => e.node.id === variantId)?.node.title || 'Default'
          : variant.title;
          
        return [
          ...prevCart,
          {
            id: product.id,
            title: product.title,
            variantId: variantId,
            variantTitle: variantTitle,
            price: price,
            image: imageUrl,
            quantity,
          },
        ];
      }
    });
  };

  // Remove item from cart
  const removeFromCart = (variantId) => {
    setCart((prevCart) => prevCart.filter((item) => item.variantId !== variantId));
  };

  // Update item quantity
  const updateQuantity = (variantId, quantity) => {
    if (quantity < 1) return;
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.variantId === variantId ? { ...item, quantity } : item
      )
    );
  };

  // Clear cart
  const clearCart = () => {
    setCart([]);
  };

  const checkout = async () => {
    if (cart.length === 0) {
      console.error('Cannot checkout with empty cart');
      return;
    }
    try {
      console.log('Cart state:', cart);

      // Build line items from the local cart state.
      const lineItems = cart.map((item) => ({
        merchandiseId: item.variantId,
        quantity: item.quantity,
      }));
      
      console.log('Preparing checkout with line items:', lineItems);
      
      // Create or update the cart on Shopify using the two-step process.
      const cartSession = await createCartAndAddItems(lineItems);
      console.log('Cart session created/updated via two-step:', cartSession);
      
      if (!cartSession || !cartSession.checkoutUrl) {
        console.error('Checkout failed: No valid checkout URL returned');
        alert('An error occurred during checkout. Please try again later.');
        return;
      }
      
      // Check if on mobile device and open in same window if so
      if (isMobile()) {
        window.location.href = cartSession.checkoutUrl;
      } else {
        // On desktop, open in new tab as before
        window.open(cartSession.checkoutUrl, '_blank');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('An error occurred during checkout. Please try again later.');
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        cartCount,
        cartTotal,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        checkout,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
